import React, { useMemo, useRef } from 'react';
import Editor from '@monaco-editor/react';
import { Box, Text, useMantineColorScheme } from '@mantine/core';
import type { editor } from 'monaco-editor';

interface CodeEditorProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  language?: string;
}

export const CodeEditor: React.FC<CodeEditorProps> = ({
  value,
  onChange,
  label,
  placeholder,
  language = 'html',
}) => {
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const scrollPositionRef = useRef<{
    scrollLeft: number;
    scrollTop: number;
  } | null>(null);

  const handleEditorChange = (value: string | undefined) => {
    onChange(value || '');
  };

  const handleEditorDidMount = (editor: editor.IStandaloneCodeEditor) => {
    editorRef.current = editor;

    // Preserve scroll position on blur
    editor.onDidBlurEditorText(() => {
      const scrollLeft = editor.getScrollLeft();
      const scrollTop = editor.getScrollTop();
      scrollPositionRef.current = { scrollLeft, scrollTop };
    });

    // Restore scroll position on focus
    editor.onDidFocusEditorText(() => {
      if (scrollPositionRef.current) {
        editor.setScrollLeft(scrollPositionRef.current.scrollLeft);
        editor.setScrollTop(scrollPositionRef.current.scrollTop);
      }
    });
  };

  const editorOptions = useMemo<editor.IStandaloneEditorConstructionOptions>(
    () => ({
      minimap: { enabled: false },
      scrollBeyondLastLine: false,
      wordWrap: 'off',
      lineNumbers: 'on',
      fontSize: 13,
      fontFamily:
        'Monaco, Menlo, "Ubuntu Mono", Consolas, source-code-pro, monospace',
      automaticLayout: true,
      scrollbar: {
        horizontal: 'auto',
        vertical: 'auto',
        horizontalScrollbarSize: 5,
        verticalScrollbarSize: 5,
      },
      placeholder: placeholder,
      renderWhitespace: 'all',
      tabSize: 2,
      insertSpaces: true,
      fixedOverflowWidgets: true,
    }),
    [placeholder]
  );

  const theme = useMemo(() => (isDark ? 'vs-dark' : 'vs'), [isDark]);

  return (
    <Box>
      {label && (
        <Text size="sm" fw={500} mb="xs">
          {label}
        </Text>
      )}
      <Box
        style={{
          border: '1px solid var(--mantine-color-default-border)',
          borderRadius: 'var(--mantine-radius-sm)',
          overflow: 'hidden',
        }}
      >
        <Editor
          height="80vh"
          language={language}
          value={value}
          onChange={handleEditorChange}
          theme={theme}
          options={editorOptions}
          onMount={handleEditorDidMount}
        />
      </Box>
    </Box>
  );
};
